import { Link } from "react-router-dom";
import {
  useProjects,
  useActionItemsWithoutProject,
  useUpdateActionItem,
  useCreateActionItem,
} from "../hooks/useData";
import { ActionableItemsView } from "../components_v2";
import type { ProjectType, ActionItemType } from "../components_v2";

export default function ActionItemsPage() {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: globalActionItems = [], isLoading: globalLoading } =
    useActionItemsWithoutProject();

  const updateActionItemMutation = useUpdateActionItem();
  const createActionItemMutation = useCreateActionItem();

  // Convert data to v2 format
  const v2Projects: ProjectType[] = projects.map((p) => ({
    id: p.id,
    name: p.name,
    desiredOutcome: p.desiredOutcome,
    actionItems:
      p.actionItems?.map((item) => ({
        id: Number(item.id),
        content: item.content,
        status: item.status || "actionable",
        date: item.date,
        delegatedTo: item.delegatedTo,
      })) || [],
  }));

  const v2GlobalActionItems: ActionItemType[] = globalActionItems.map(
    (item) => ({
      id: Number(item.id),
      content: item.content,
      status: item.status || "actionable",
      date: item.date,
      delegatedTo: item.delegatedTo,
    }),
  );

  const isLoading = projectsLoading || globalLoading;

  // Handler functions for the ActionableItemsView component
  const handleUpdateProject = (project: ProjectType) => {
    // Update action items within the project through the action item API
    project.actionItems.forEach((actionItem) => {
      const originalProject = projects.find((p) => p.id === project.id);
      const originalActionItem = originalProject?.actionItems?.find(
        (item) => Number(item.id) === actionItem.id,
      );

      // Only update if the action item has changed
      if (
        originalActionItem &&
        (originalActionItem.content !== actionItem.content ||
          originalActionItem.status !== actionItem.status ||
          originalActionItem.date !== actionItem.date ||
          originalActionItem.delegatedTo !== actionItem.delegatedTo)
      ) {
        updateActionItemMutation.mutate({
          id: actionItem.id,
          content: actionItem.content,
          status: actionItem.status,
          date: actionItem.date,
          delegatedTo: actionItem.delegatedTo,
          projectId: project.id,
        });
      }
    });
  };

  const handleUpdateGlobalActionItems = (items: ActionItemType[]) => {
    items.forEach((actionItem) => {
      const originalActionItem = globalActionItems.find(
        (item) => Number(item.id) === actionItem.id,
      );

      if (actionItem.id === -1 || !originalActionItem) {
        // This is a new action item, create it
        createActionItemMutation.mutate({
          content: actionItem.content,
          status: actionItem.status,
          date: actionItem.date,
          delegatedTo: actionItem.delegatedTo,
        });
      } else if (
        originalActionItem.content !== actionItem.content ||
        originalActionItem.status !== actionItem.status ||
        originalActionItem.date !== actionItem.date ||
        originalActionItem.delegatedTo !== actionItem.delegatedTo
      ) {
        // Update existing action item
        updateActionItemMutation.mutate({
          id: actionItem.id,
          content: actionItem.content,
          status: actionItem.status,
          date: actionItem.date,
          delegatedTo: actionItem.delegatedTo,
        });
      }
    });
  };

  if (isLoading) {
    return (
      <main className="font-sans m-8">
        <div>Loading...</div>
      </main>
    );
  }

  return (
    <main className="font-sans m-8">
      <div className="mb-4">
        <Link to="/" className="text-blue-600 hover:underline">
          ← Back to Home
        </Link>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">All Action Items</h1>
      </div>

      {/* V2 Actionable Items View */}
      <ActionableItemsView
        projects={v2Projects}
        globalActionItems={v2GlobalActionItems}
        onUpdateProject={handleUpdateProject}
        onUpdateGlobalActionItems={handleUpdateGlobalActionItems}
      />
    </main>
  );
}
